/**
 * Marketing Router
 * Handles all marketing routes with variant support and visitor detection
 */

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Theme, Loading } from '@carbon/react';
import { useTheme } from '../contexts/ThemeContext';

// Lazy load marketing page components for code splitting and performance
const MarketingLandingPage = lazy(() => import('../pages/marketing/MarketingLandingPage'));
const SEOOptimizedLandingPage = lazy(() => import('../components/SEOOptimizedLandingPage'));
const FeaturesPage = lazy(() => import('../pages/marketing/FeaturesPage'));
const PricingPage = lazy(() => import('../pages/marketing/PricingPage'));
const DemoRequestPage = lazy(() => import('../pages/marketing/DemoRequestPage'));
const TrialSignupPage = lazy(() => import('../pages/marketing/TrialSignupPage'));
const ValuePropositionPage = lazy(() => import('../pages/marketing/ValuePropositionPage'));
const SuccessStoriesPage = lazy(() => import('../pages/marketing/SuccessStoriesPage'));

// Lazy load calculators page with webpackChunkName for better debugging
const CalculatorsPage = lazy(() => import(/* webpackChunkName: "calculators" */ '../pages/marketing/CalculatorsPage'));

// Lazy load institution landing pages
const InstitutionLandingPage = lazy(() => import('../pages/marketing/InstitutionLandingPage'));

// Lazy load parent landing page
const ParentLanding = lazy(() => import('../pages/marketing/ParentLanding'));

// Lazy load high-performance landing page
const HighPerformanceLandingPage = lazy(() => import('../pages/marketing/HighPerformanceLandingPage'));

// SEO and Analytics
import { MarketingSEO } from '../components/marketing/MarketingSEO';
import { useMarketingAnalytics } from '../hooks/useMarketingAnalytics';
import { useVisitorDetection } from '../hooks/useVisitorDetection';

// Types
export type MarketingVariant = 'default' | 'educator' | 'parent' | 'administrator';

interface MarketingRoute {
  path: string;
  component: React.ComponentType<{ variant: MarketingVariant }>;
  title: string;
  description: string;
  keywords: string[];
}

// Lazy load educational content page
const EducationalContentPage = lazy(() => import('../pages/content/EducationalContentPage'));

// Lazy load public courses page
const PublicCoursesPage = lazy(() => import('../pages/marketing/PublicCoursesPage'));

const marketingRoutes: MarketingRoute[] = [
  {
    path: '/',
    component: MarketingLandingPage,
    title: 'Sigma08 - AI-Powered Learning Platform',
    description: 'Transform education with AI-powered personalized learning. Join 10,000+ educators using Sigma08.',
    keywords: ['AI education', 'personalized learning', 'educational technology'],
  },
  {
    path: '/features',
    component: FeaturesPage,
    title: 'Features - Sigma08 Learning Platform',
    description: 'Explore powerful features including AI tutoring, real-time analytics, and family collaboration.',
    keywords: ['education features', 'AI tutoring', 'learning analytics'],
  },
  {
    path: '/pricing',
    component: PricingPage,
    title: 'Pricing - Sigma08 Learning Platform',
    description: 'Flexible pricing plans for educators, schools, and districts. Start your free 30-day trial.',
    keywords: ['education pricing', 'school pricing', 'district pricing'],
  },
  {
    path: '/value',
    component: ValuePropositionPage,
    title: 'Educational Value Proposition - Sigma08 Learning Platform',
    description: 'Discover measurable educational impact with proven results from real institutions using Sigma08.',
    keywords: ['educational value', 'learning outcomes', 'student achievement', 'educational impact', 'proven results'],
  },
  {
    path: '/success-stories',
    component: SuccessStoriesPage,
    title: 'Success Stories - Sigma08 Learning Platform',
    description:
      'Read transformational success stories from schools and districts achieving remarkable outcomes with Sigma08.',
    keywords: ['success stories', 'case studies', 'educational transformation', 'school success', 'district results'],
  },
  {
    path: '/content',
    component: EducationalContentPage,
    title: 'Educational Content Library - Sigma08 Learning Platform',
    description:
      'Explore comprehensive educational resources, guides, research articles, and best practices for AI-powered learning.',
    keywords: [
      'educational content',
      'learning resources',
      'teaching guides',
      'AI tutoring',
      'personalized education',
      'educational research',
    ],
  },
  {
    path: '/calculators',
    component: CalculatorsPage,
    title: 'Educational Calculators - Sigma08 Learning Platform',
    description: 'Calculate ROI, time savings, student outcomes, and cost-benefit analysis for your educational needs.',
    keywords: ['education calculators', 'ROI calculator', 'time savings', 'student outcomes', 'cost benefit'],
  },
  {
    path: '/demo',
    component: DemoRequestPage,
    title: 'Request Demo - Sigma08 Learning Platform',
    description: 'Schedule a personalized demo to see how Sigma08 can transform your educational experience.',
    keywords: ['demo request', 'education demo', 'platform demonstration'],
  },
  {
    path: '/trial',
    component: TrialSignupPage,
    title: 'Start Free Trial - Sigma08 Learning Platform',
    description: 'Start your 30-day free trial. No credit card required. Full access to all features.',
    keywords: ['free trial', 'education trial', 'try sigma08'],
  },
];

const variantRoutes = [
  { path: '/educators', variant: 'educator' as MarketingVariant },
  { path: '/parents', variant: 'parent' as MarketingVariant },
  { path: '/administrators', variant: 'administrator' as MarketingVariant },
];

export const MarketingRouter: React.FC = () => {
  const location = useLocation();
  const { trackPageView, trackEvent } = useMarketingAnalytics();
  const { detectedVariant, detectVisitor } = useVisitorDetection();
  const [currentVariant, setCurrentVariant] = useState<MarketingVariant>('default');
  const { theme } = useTheme();

  // Detect visitor type on mount and route changes
  useEffect(() => {
    const variant = detectVisitor(location.pathname);
    setCurrentVariant(variant);

    // Track page view with variant
    trackPageView({
      path: location.pathname,
      variant,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
    });
  }, [location.pathname, detectVisitor, trackPageView]);

  // Handle dynamic routing based on visitor detection
  useEffect(() => {
    // If we detected a specific visitor type and they're on the homepage
    if (detectedVariant !== 'default' && location.pathname === '/') {
      const variantPath = `/${detectedVariant}s`;

      // Only redirect if the variant route exists
      if (variantRoutes.find(r => r.path === variantPath)) {
        trackEvent({
          category: 'Navigation',
          action: 'Auto-redirect',
          label: `Detected ${detectedVariant}`,
          value: 1,
        });
      }
    }
  }, [detectedVariant, location.pathname, trackEvent]);

  return (
    <Theme theme="white">
      <Routes>
        {/* Default marketing routes */}
        {marketingRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={
              <>
                <MarketingSEO
                  title={route.title}
                  description={route.description}
                  keywords={route.keywords}
                  path={route.path}
                  variant={currentVariant}
                />
                <Suspense
                  fallback={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '50vh',
                      }}
                    >
                      <Loading description="Loading..." withOverlay={false} />
                    </div>
                  }
                >
                  <route.component variant={currentVariant} />
                </Suspense>
              </>
            }
          />
        ))}

        {/* Educational Content Routes with Dynamic Paths */}
        <Route
          path="/content/:contentType/:contentId"
          element={
            <Suspense fallback={<Loading description="Loading content..." />}>
              <EducationalContentPage />
            </Suspense>
          }
        />
        <Route
          path="/content/:contentType"
          element={
            <Suspense fallback={<Loading description="Loading content..." />}>
              <EducationalContentPage />
            </Suspense>
          }
        />
        <Route
          path="/articles"
          element={
            <>
              <MarketingSEO
                title="Educational Articles - Sigma08 Learning Platform"
                description="Read expert articles on AI tutoring, personalized learning, and educational technology."
                keywords={['educational articles', 'AI education insights', 'learning research']}
                path="/articles"
                variant={currentVariant}
              />
              <Suspense fallback={<Loading description="Loading articles..." />}>
                <EducationalContentPage />
              </Suspense>
            </>
          }
        />
        <Route
          path="/guides"
          element={
            <>
              <MarketingSEO
                title="Educational Guides - Sigma08 Learning Platform"
                description="Comprehensive guides for parents, educators, and administrators on implementing AI-powered learning."
                keywords={['educational guides', 'implementation guides', 'best practices']}
                path="/guides"
                variant={currentVariant}
              />
              <Suspense fallback={<Loading description="Loading guides..." />}>
                <EducationalContentPage />
              </Suspense>
            </>
          }
        />
        <Route
          path="/research"
          element={
            <>
              <MarketingSEO
                title="Educational Research - Sigma08 Learning Platform"
                description="Scientific research and case studies on personalized education and learning outcomes."
                keywords={['educational research', 'learning science', 'academic studies']}
                path="/research"
                variant={currentVariant}
              />
              <Suspense fallback={<Loading description="Loading research..." />}>
                <EducationalContentPage />
              </Suspense>
            </>
          }
        />

        {/* Marketing variant landing pages */}
        <Route
          path="/marketing/trial"
          element={
            <>
              <MarketingSEO
                title="Start Free Trial - Sigma08 Learning Platform"
                description="Start your 30-day free trial. No credit card required. Full access to all features."
                keywords={['free trial', 'education trial', 'try sigma08']}
                path="/marketing/trial"
                variant={currentVariant}
              />
              <TrialSignupPage variant={currentVariant} />
            </>
          }
        />
        <Route
          path="/marketing/signup"
          element={
            <>
              <MarketingSEO
                title="Sign Up - Sigma08 Learning Platform"
                description="Create your account and join thousands of educators using Sigma08 AI-powered learning."
                keywords={['signup', 'create account', 'join sigma08']}
                path="/marketing/signup"
                variant={currentVariant}
              />
              <TrialSignupPage variant={currentVariant} />
            </>
          }
        />
        <Route
          path="/marketing/high-performance"
          element={
            <>
              <MarketingSEO
                title="High Performance Learning - Sigma08 Educational Platform"
                description="Achieve exceptional educational outcomes with our high-performance AI-powered learning platform."
                keywords={['high performance learning', 'educational excellence', 'AI tutoring', 'student achievement']}
                path="/marketing/high-performance"
                variant="default"
              />
              <HighPerformanceLandingPage variant="main" />
            </>
          }
        />
        <Route
          path="/marketing/institution"
          element={
            <>
              <MarketingSEO
                title="Institution Landing - Sigma08 Educational Platform"
                description="AI-powered personalized learning for educational institutions. Transform your learning outcomes."
                keywords={['institution education', 'educational platform', 'AI learning']}
                path="/marketing/institution"
                variant="educator"
              />
              <HighPerformanceLandingPage variant="institution" />
            </>
          }
        />
        <Route
          path="/marketing/parent"
          element={
            <>
              <MarketingSEO
                title="Parent Portal - Sigma08 Family Learning"
                description="Stay connected to your child's learning journey with AI-powered insights and support."
                keywords={['parent portal', 'family learning', 'student progress', 'parent engagement']}
                path="/marketing/parent"
                variant="parent"
              />
              <ParentLanding />
            </>
          }
        />

        {/* Institution-specific landing pages */}
        {/* Legacy institution paths for backward compatibility */}
        <Route
          path="/institutions/k12"
          element={
            <>
              <MarketingSEO
                title="K-12 Schools - Sigma08 Educational Platform"
                description="AI-powered personalized learning for K-12 schools. Improve student outcomes with adaptive tutoring."
                keywords={['K-12 education', 'school AI platform', 'student learning']}
                path="/institutions/k12"
                variant="educator"
              />
              <InstitutionLandingPage />
            </>
          }
        />
        <Route
          path="/institutions/higher-ed"
          element={
            <>
              <MarketingSEO
                title="Higher Education - Sigma08 University Platform"
                description="Scale personalized learning at universities with AI tutoring and research support."
                keywords={['higher education', 'university AI', 'college learning platform']}
                path="/institutions/higher-ed"
                variant="educator"
              />
              <InstitutionLandingPage />
            </>
          }
        />
        <Route
          path="/solutions/k12"
          element={
            <>
              <MarketingSEO
                title="K-12 Schools - Sigma08 Educational Platform"
                description="AI-powered personalized learning for K-12 schools. Improve student outcomes with adaptive tutoring."
                keywords={['K-12 education', 'school AI platform', 'student learning']}
                path="/solutions/k12"
                variant="educator"
              />
              <InstitutionLandingPage />
            </>
          }
        />
        <Route
          path="/solutions/higher-ed"
          element={
            <>
              <MarketingSEO
                title="Higher Education - Sigma08 University Platform"
                description="Scale personalized learning at universities with AI tutoring and research support."
                keywords={['higher education', 'university AI', 'college learning platform']}
                path="/solutions/higher-ed"
                variant="educator"
              />
              <InstitutionLandingPage />
            </>
          }
        />
        <Route
          path="/solutions/corporate"
          element={
            <>
              <MarketingSEO
                title="Corporate Training - Sigma08 Enterprise Learning"
                description="Transform corporate training with AI-powered personalized employee development."
                keywords={['corporate training', 'employee development', 'enterprise learning']}
                path="/solutions/corporate"
                variant="administrator"
              />
              <InstitutionLandingPage />
            </>
          }
        />

        {/* Variant-specific routes */}
        {variantRoutes.map(({ path, variant }) => (
          <Route
            key={path}
            path={path}
            element={
              <>
                <MarketingSEO
                  title={`Sigma08 for ${variant.charAt(0).toUpperCase() + variant.slice(1)}s`}
                  description={getVariantDescription(variant)}
                  keywords={getVariantKeywords(variant)}
                  path={path}
                  variant={variant}
                />
                <MarketingLandingPage variant={variant} />
              </>
            }
          />
        ))}

        {/* About and Contact pages */}
        <Route
          path="/about"
          element={
            <>
              <MarketingSEO
                title="About Sigma08 - AI Educational Platform"
                description="Learn about our mission to transform education with AI-powered personalized learning."
                keywords={['about sigma08', 'educational technology company', 'AI education mission']}
                path="/about"
                variant={currentVariant}
              />
              <Suspense fallback={<Loading description="Loading page..." />}>
                <SEOOptimizedLandingPage />
              </Suspense>
            </>
          }
        />
        <Route
          path="/contact"
          element={
            <>
              <MarketingSEO
                title="Contact Sigma08 - Get in Touch"
                description="Contact our team for questions, support, or to schedule a personalized demo."
                keywords={['contact sigma08', 'education support', 'get demo']}
                path="/contact"
                variant={currentVariant}
              />
              <Suspense fallback={<Loading description="Loading contact page..." />}>
                <DemoRequestPage variant={currentVariant} />
              </Suspense>
            </>
          }
        />

        {/* Public courses page */}
        <Route
          path="/public/courses"
          element={
            <>
              <MarketingSEO
                title="Public Courses - Sigma08 Learning Platform"
                description="Browse our comprehensive catalog of public courses covering AI education, personalized learning, and modern teaching methods."
                keywords={['public courses', 'online learning', 'education courses', 'AI tutoring', 'teacher training']}
                path="/public/courses"
                variant={currentVariant}
              />
              <Suspense fallback={<Loading description="Loading courses..." />}>
                <PublicCoursesPage />
              </Suspense>
            </>
          }
        />

        {/* Redirects for common patterns */}
        <Route path="/teachers" element={<Navigate to="/educators" replace />} />
        <Route path="/schools" element={<Navigate to="/administrators" replace />} />
        <Route path="/districts" element={<Navigate to="/administrators" replace />} />
        <Route path="/families" element={<Navigate to="/parents" replace />} />
      </Routes>
    </Theme>
  );
};

// Helper functions
function getVariantDescription(variant: MarketingVariant): string {
  switch (variant) {
    case 'educator':
      return 'Transform your classroom with AI teaching tools. Join 15,000+ teachers using Sigma08.';
    case 'parent':
      return "Stay connected to your child's learning journey with real-time insights and support.";
    case 'administrator':
      return 'Drive district-wide educational excellence with comprehensive analytics and tools.';
    default:
      return 'Transform education with AI-powered personalized learning.';
  }
}

function getVariantKeywords(variant: MarketingVariant): string[] {
  switch (variant) {
    case 'educator':
      return ['teacher tools', 'classroom AI', 'lesson planning', 'automated grading'];
    case 'parent':
      return ['parent portal', 'student progress', 'homework help', 'family engagement'];
    case 'administrator':
      return ['school administration', 'district analytics', 'compliance management', 'resource optimization'];
    default:
      return ['AI education', 'personalized learning', 'educational technology'];
  }
}

export default MarketingRouter;
